/**
 * TelegramChannelsManagement — «کانال‌های تلگرام فروشگاه‌ها»
 *
 * کدام فروشگاه‌ها ربات کازماشاپ را در کانال/گروه تلگرام‌شان ادمین کرده‌اند،
 * آدرس کانال، سلامت ردیابی، عملکرد (پست، اعضا، فروش ۳۰ روزه از تلگرام) و
 * ابزار مدیریت: بررسی سلامت از تلگرام، همگام‌سازی، انتشار خودکار، تعلیق،
 * لینک ورود یک‌بارمصرف (کانال خصوصی)، یادداشت داخلی و پاک‌کردن رکورد قطع‌شده.
 * تب دوم: فروشگاه‌های فعالی که هنوز کانالی وصل نکرده‌اند.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TelegramIcon from '@mui/icons-material/Telegram';
import SyncIcon from '@mui/icons-material/Sync';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LinkIcon from '@mui/icons-material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useSnackbar } from 'notistack';
import { PageHeader, ResponsiveTable, ConfirmDialog, ResponsiveModal, StatsCard, Column } from '../components/common';
import Gate from '../components/access/Gate';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import { formatDate } from '../utils/dateUtils';
import telegramChannelsService, {
  TG_STATUS_COLOR,
  TG_STATUS_LABELS,
  TgChannelDetail,
  TgChannelRow,
  TgOverview,
  TgStoreWithoutChannel,
} from '../services/telegramChannels.service';
import { WEB_BASE_URL } from '../config/env';

const fa = (v: number | string) => toPersianDigits(String(v));
const toman = (n: number) => `${(n || 0).toLocaleString('fa-IR')} تومان`;
const errMsg = (e: any) => e?.response?.data?.message || e?.message || 'خطا';
const MANAGE = 'admin_telegram_channels_manage';

const SITE_URL = WEB_BASE_URL;

const ACTIVITY_COLOR: Record<string, 'default' | 'warning' | 'error'> = {
  info: 'default',
  warn: 'warning',
  error: 'error',
};

const SOLD_OUT_LABEL: Record<string, string> = {
  label: 'برچسب «فروخته شد»',
  delete: 'حذف پست',
  keep: 'دست‌نخورده',
};

const MODERATION_LABEL: Record<string, string> = {
  off: 'خاموش',
  delete: 'فقط حذف پیام',
  warn: 'حذف + اخطار + مجازات',
};

const POST_STATUS_LABEL: Record<string, string> = {
  pending: 'در صف',
  active: 'فعال',
  sold: 'فروخته شد',
  removed: 'حذف‌شده',
};

const copy = async (text: string, enqueue: (m: string, o?: any) => void) => {
  try {
    await navigator.clipboard.writeText(text);
    enqueue('کپی شد', { variant: 'success' });
  } catch {
    enqueue('کپی نشد', { variant: 'error' });
  }
};

/** نام و آدرس کانال — قلب این ماژول */
const ChannelAddress: React.FC<{ row: Pick<TgChannelRow, 'title' | 'username' | 'publicUrl' | 'privateUrl' | 'chatId'> }> = ({ row }) => (
  <Box>
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{row.title || 'بدون نام'}</Typography>
    {row.username ? (
      <Link href={row.publicUrl || '#'} target="_blank" rel="noopener" underline="hover" dir="ltr" sx={{ fontSize: '0.78rem' }}>
        @{row.username}
      </Link>
    ) : (
      <Tooltip title="کانال خصوصی است؛ این لینک فقط برای اعضا باز می‌شود. برای ورود از «لینک ورود» در جزئیات استفاده کنید.">
        <Link href={row.privateUrl} target="_blank" rel="noopener" underline="hover" dir="ltr" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          خصوصی · {row.chatId}
        </Link>
      </Tooltip>
    )}
  </Box>
);

// ───────────────────────────────────────────── تب کانال‌ها

const ChannelsTab: React.FC<{ onOpen: (chatId: number) => void; reloadKey: number }> = ({ onOpen, reloadKey }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<TgChannelRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [chatType, setChatType] = useState('');
  const [ownerType, setOwnerType] = useState('store');
  const [purpose, setPurpose] = useState('');
  const [sort, setSort] = useState('newest');

  const filters = { q: query, status, chatType, ownerType, purpose, sort };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await telegramChannelsService.list({ ...filters, page: page + 1, limit: rowsPerPage });
      setRows(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, status, chatType, ownerType, purpose, sort, page, rowsPerPage, enqueueSnackbar, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      setQuery(q.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await telegramChannelsService.list({ ...filters, page: 1, limit: 1000 });
      const head = ['فروشگاه', 'مالک', 'موبایل', 'تلگرام مالک', 'نام کانال', 'آدرس', 'شناسه', 'نوع', 'کاربرد', 'وضعیت', 'انتشار خودکار', 'اعضا', 'پست فعال', 'پست ۳۰ روز', 'سفارش ۳۰ روز', 'فروش ۳۰ روز (تومان)', 'تاریخ اتصال'];
      const lines = (res.items || []).map((r) => [
        r.store?.name || (r.ownerType === 'platform' ? 'کازماشاپ (پلتفرمی)' : ''),
        r.owner?.name || '',
        r.owner?.phone || '',
        r.owner?.telegram?.username ? `@${r.owner.telegram.username}` : '',
        r.title || '',
        r.publicUrl || r.privateUrl,
        r.chatId,
        r.chatType === 'group' ? 'گروه' : 'کانال',
        r.purpose === 'community' ? 'گفتگو' : 'فروش',
        TG_STATUS_LABELS[r.status],
        r.autoPublish ? 'روشن' : 'خاموش',
        r.memberCount ?? '',
        r.stats.activePosts,
        r.stats.posts30d,
        r.stats.orders30d,
        r.stats.revenue30d,
        r.connectedAt ? formatDate(r.connectedAt) : '',
      ]);
      const csv = [head, ...lines]
        .map((l) => l.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `telegram-channels-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const columns: Column<TgChannelRow>[] = [
    {
      id: 'store',
      label: 'فروشگاه',
      priority: 1,
      format: (_, r) =>
        r.store ? (
          <Stack direction="row" gap={1} alignItems="center">
            <Avatar src={r.store.logo || undefined} sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
              {r.store.name?.[0]}
            </Avatar>
            <Box>
              <Link href={`${SITE_URL}/store/${r.store.slug}`} target="_blank" rel="noopener" underline="hover" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                {r.store.name}
              </Link>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                {r.owner?.name || '—'}
                {r.owner?.phone ? <span dir="ltr"> · {r.owner.phone}</span> : null}
              </Typography>
              {r.owner?.telegram?.username && (
                <Link href={`https://t.me/${r.owner.telegram.username}`} target="_blank" rel="noopener" dir="ltr" sx={{ fontSize: '0.72rem' }}>
                  @{r.owner.telegram.username}
                </Link>
              )}
            </Box>
          </Stack>
        ) : (
          <Chip size="small" color="primary" variant="outlined" label="کانال رسمی کازماشاپ" />
        ),
    },
    {
      id: 'title',
      label: 'کانال / گروه',
      priority: 1,
      format: (_, r) => (
        <Box>
          <ChannelAddress row={r} />
          <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
            <Chip size="small" label={r.chatType === 'group' ? 'گروه' : 'کانال'} sx={{ height: 20, fontSize: '0.65rem' }} />
            {r.purpose === 'community' && <Chip size="small" variant="outlined" label="گفتگو" sx={{ height: 20, fontSize: '0.65rem' }} />}
            {r.adminNote && (
              <Tooltip title={r.adminNote}>
                <Chip size="small" color="info" variant="outlined" label="یادداشت" sx={{ height: 20, fontSize: '0.65rem' }} />
              </Tooltip>
            )}
          </Stack>
        </Box>
      ),
    },
    {
      id: 'memberCount',
      label: 'اعضا',
      priority: 2,
      format: (v, r) => (
        <Tooltip title={r.memberCountAt ? `آخرین شمارش: ${formatDate(r.memberCountAt)}` : 'هنوز شمارش نشده'}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{v == null ? '—' : fa(v.toLocaleString())}</Typography>
        </Tooltip>
      ),
    },
    {
      id: 'posts',
      label: 'پست‌ها',
      priority: 3,
      format: (_, r) => (
        <Box>
          <Typography sx={{ fontSize: '0.8rem' }}>{fa(r.stats.activePosts)} فعال · {fa(r.stats.posts30d)} در ۳۰ روز</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
            آخرین: {r.stats.lastPostAt ? formatDate(r.stats.lastPostAt) : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'sales',
      label: 'فروش ۳۰ روز',
      priority: 3,
      format: (_, r) => (
        <Box>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{toman(r.stats.revenue30d)}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
            {fa(r.stats.orders30d)} سفارش · {fa(r.stats.clicks)} کلیک
          </Typography>
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'وضعیت',
      priority: 2,
      format: (_, r) => (
        <Stack gap={0.5} alignItems="flex-start">
          <Tooltip title={r.suspended?.reason || r.trackingIssue || ''}>
            <Chip size="small" color={TG_STATUS_COLOR[r.status]} label={TG_STATUS_LABELS[r.status]} />
          </Tooltip>
          {r.active && r.purpose !== 'community' && !r.autoPublish && (
            <Chip size="small" variant="outlined" color="warning" label="انتشار خودکار خاموش" sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
          {r.stats.errors24h > 0 && (
            <Chip size="small" variant="outlined" color="error" label={`${fa(r.stats.errors24h)} خطا در ۲۴ساعت`} sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
        </Stack>
      ),
    },
    {
      id: 'connectedAt',
      label: 'اتصال',
      priority: 4,
      hideOnMobile: true,
      format: (v) => <Typography sx={{ fontSize: '0.75rem' }}>{v ? formatDate(v) : '—'}</Typography>,
    },
  ];

  const sel = (label: string, value: string, set: (v: string) => void, opts: Array<[string, string]>, width = 140) => (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => {
        setPage(0);
        set(e.target.value);
      }}
      sx={{ minWidth: width }}
    >
      {opts.map(([v, l]) => (
        <MenuItem key={v} value={v}>
          {l}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} sx={{ mb: 2 }} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="جستجو: نام کانال، @یوزرنیم، شناسه یا نام فروشگاه"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 300, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {sel('وضعیت', status, setStatus, [['', 'همه'], ['active', 'فعال'], ['issue', 'مشکل ردیابی'], ['suspended', 'معلق'], ['disconnected', 'قطع‌شده']])}
        {sel('مالک', ownerType, setOwnerType, [['', 'همه'], ['store', 'فروشگاه‌ها'], ['platform', 'رسمی کازماشاپ']])}
        {sel('نوع', chatType, setChatType, [['', 'کانال و گروه'], ['channel', 'کانال'], ['group', 'گروه']])}
        {sel('کاربرد', purpose, setPurpose, [['', 'همه'], ['sales', 'فروش'], ['community', 'گفتگو']])}
        {sel('مرتب‌سازی', sort, setSort, [['newest', 'جدیدترین اتصال'], ['oldest', 'قدیمی‌ترین'], ['members', 'بیشترین عضو'], ['title', 'نام کانال']], 160)}
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={exporting}>
          خروجی CSV
        </Button>
      </Stack>

      <ResponsiveTable<TgChannelRow>
        columns={columns}
        data={rows}
        loading={loading}
        keyField="id"
        emptyMessage="هیچ کانال یا گروهی با این فیلتر پیدا نشد"
        pagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={total}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
        onRowClick={(r) => onOpen(r.chatId)}
        actionsLabel="عملیات"
        renderActions={(r) => (
          <Stack direction="row" gap={0.5}>
            <Tooltip title="جزئیات و مدیریت">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpen(r.chatId); }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={r.username ? 'باز کردن در تلگرام' : 'لینک خصوصی (فقط اعضا)'}>
              <IconButton size="small" component="a" href={r.publicUrl || r.privateUrl} target="_blank" rel="noopener" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      />
    </Box>
  );
};

// ───────────────────────────────────────────── تب فروشگاه‌های بدون کانال

const NoChannelTab: React.FC<{ reloadKey: number }> = ({ reloadKey }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<TgStoreWithoutChannel[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      setQuery(q.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    telegramChannelsService
      .storesWithoutChannel({ q: query || undefined, page: page + 1, limit: rowsPerPage })
      .then((res) => {
        if (!alive) return;
        setRows(res.items || []);
        setTotal(res.total || 0);
      })
      .catch((e) => enqueueSnackbar(errMsg(e), { variant: 'error' }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [query, page, rowsPerPage, reloadKey, enqueueSnackbar]);

  const columns: Column<TgStoreWithoutChannel>[] = [
    {
      id: 'name',
      label: 'فروشگاه',
      priority: 1,
      format: (_, r) => (
        <Stack direction="row" gap={1} alignItems="center">
          <Avatar src={r.logo || undefined} sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
            {r.name?.[0]}
          </Avatar>
          <Link href={`${SITE_URL}/store/${r.slug}`} target="_blank" rel="noopener" underline="hover" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
            {r.name}
          </Link>
        </Stack>
      ),
    },
    {
      id: 'owner',
      label: 'مالک',
      priority: 2,
      format: (_, r) => (
        <Box>
          <Typography sx={{ fontSize: '0.8rem' }}>{r.owner?.name || '—'}</Typography>
          {r.owner?.phone && (
            <Typography dir="ltr" sx={{ fontSize: '0.72rem', color: 'text.secondary', textAlign: 'right' }}>
              {r.owner.phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'telegramLinked',
      label: 'حساب تلگرام',
      priority: 2,
      format: (_, r) =>
        r.telegramLinked ? (
          <Tooltip title="ربات را می‌شناسد؛ فقط کافی است ربات را ادمین کانالش کند">
            <Chip size="small" color="success" variant="outlined" label={r.telegramUsername ? `@${r.telegramUsername}` : 'وصل است'} />
          </Tooltip>
        ) : (
          <Chip size="small" variant="outlined" label="وصل نیست" />
        ),
    },
    {
      id: 'previousChannel',
      label: 'سابقه',
      priority: 3,
      format: (_, r) =>
        r.previousChannel ? (
          <Tooltip title={`آخرین تغییر: ${formatDate(r.previousChannel.at)}`}>
            <Chip
              size="small"
              color={r.previousChannel.suspended ? 'error' : 'default'}
              label={`${r.previousChannel.suspended ? 'معلق' : 'قطع شده'}: ${r.previousChannel.title || r.previousChannel.username || 'کانال'}`}
            />
          </Tooltip>
        ) : (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>هرگز وصل نکرده</Typography>
        ),
    },
    { id: 'createdAt', label: 'ساخت فروشگاه', priority: 4, hideOnMobile: true, format: (v) => (v ? formatDate(v) : '—') },
  ];

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        فروشگاه‌های فعالی که هیچ کانال یا گروه فعالی به کازماشاپ وصل نکرده‌اند. آن‌هایی که «حساب تلگرام» وصل دارند
        فقط یک قدم فاصله دارند: ادمین‌کردن ربات در کانال‌شان (راهنما در پنل فروشنده، بخش تلگرام).
      </Alert>
      <TextField
        size="small"
        placeholder="جستجوی نام فروشگاه"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        sx={{ mb: 2, minWidth: 300 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <ResponsiveTable<TgStoreWithoutChannel>
        columns={columns}
        data={rows}
        loading={loading}
        keyField="id"
        emptyMessage="همهٔ فروشگاه‌های فعال کانال وصل کرده‌اند 🎉"
        pagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={total}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
      />
    </Box>
  );
};

// ───────────────────────────────────────────── جزئیات کانال

const Kv: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <Stack direction="row" justifyContent="space-between" gap={2} sx={{ py: 0.5 }}>
    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{k}</Typography>
    <Box sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'left' }}>{v}</Box>
  </Stack>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
    <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

const ChannelDetailModal: React.FC<{ chatId: number | null; onClose: () => void; onChanged: () => void }> = ({ chatId, onClose, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [d, setD] = useState<TgChannelDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [invite, setInvite] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (chatId == null) return;
    setLoading(true);
    try {
      const data = await telegramChannelsService.detail(chatId);
      setD(data);
      setNote(data.adminNote || '');
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [chatId, enqueueSnackbar]);

  useEffect(() => {
    setD(null);
    setInvite(null);
    load();
  }, [load]);

  const run = async (key: string, fn: () => Promise<string | undefined | void>, ok?: string) => {
    setBusy(key);
    try {
      const msg = await fn();
      enqueueSnackbar(msg || ok || 'انجام شد', { variant: 'success' });
      await load();
      onChanged();
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const s = d?.summary;
  const conv = s?.conversion;
  const cov = s?.coverage;

  return (
    <ResponsiveModal
      open={chatId != null}
      onClose={onClose}
      maxWidth="md"
      loading={loading && !d}
      icon={<TelegramIcon />}
      title={d?.title || 'کانال تلگرام'}
      subtitle={d ? (d.store ? `فروشگاه ${d.store.name}` : 'کانال رسمی کازماشاپ') : undefined}
    >
      {d && (
        <Stack gap={2}>
          {d.suspended && (
            <Alert severity="error">
              معلق‌شده توسط مدیریت در {formatDate(d.suspended.at)} — دلیل: {d.suspended.reason || '—'}
            </Alert>
          )}
          {d.status === 'disconnected' && (
            <Alert severity="warning">ربات دیگر در این چت ادمین نیست (یا حذف شده)؛ هیچ پستی منتشر نمی‌شود.</Alert>
          )}
          {d.status === 'issue' && (
            <Alert severity="warning">
              {d.trackingIssue || 'ربات مجوز «دعوت کاربران» ندارد؛ ردیابی عضویت و لینک‌های دعوت کار نمی‌کند.'}
            </Alert>
          )}

          {/* آدرس + عملیات */}
          <Section title="آدرس و عملیات">
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1.5}>
              <Box>
                <ChannelAddress row={d} />
                <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                  <Typography dir="ltr" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    chat id: {d.chatId}
                  </Typography>
                  <IconButton size="small" onClick={() => copy(String(d.chatId), enqueueSnackbar)}>
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  {d.publicUrl && (
                    <IconButton size="small" onClick={() => copy(d.publicUrl!, enqueueSnackbar)}>
                      <LinkIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Stack>
                <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                  <Chip size="small" color={TG_STATUS_COLOR[d.status]} label={TG_STATUS_LABELS[d.status]} />
                  <Chip size="small" label={d.chatType === 'group' ? 'گروه' : 'کانال'} />
                  <Chip size="small" variant="outlined" label={d.purpose === 'community' ? 'گفتگو (بدون محصول)' : 'فروش'} />
                </Stack>
              </Box>
              <Gate permission={MANAGE}>
                <Stack gap={1} sx={{ minWidth: 220 }}>
                  <Button size="small" variant="outlined" startIcon={<HealthAndSafetyIcon />} disabled={!!busy}
                    onClick={() => run('refresh', async () => (await telegramChannelsService.refresh(d.chatId)).message)}>
                    بررسی سلامت از تلگرام
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<SyncIcon />} disabled={!!busy || !d.active || d.purpose === 'community'}
                    onClick={() => run('sync', () => telegramChannelsService.sync(d.chatId))}>
                    همگام‌سازی محصولات
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<LinkIcon />} disabled={!!busy || !d.active}
                    onClick={async () => {
                      setBusy('invite');
                      try {
                        setInvite(await telegramChannelsService.inviteLink(d.chatId));
                      } catch (e) {
                        enqueueSnackbar(errMsg(e), { variant: 'error' });
                      } finally {
                        setBusy(null);
                      }
                    }}>
                    لینک ورود یک‌بارمصرف
                  </Button>
                  {d.suspended ? (
                    <Button size="small" variant="contained" color="success" startIcon={<LockOpenIcon />} disabled={!!busy}
                      onClick={() => run('unsuspend', () => telegramChannelsService.unsuspend(d.chatId))}>
                      رفع تعلیق
                    </Button>
                  ) : (
                    <Button size="small" variant="outlined" color="error" startIcon={<BlockIcon />} disabled={!!busy}
                      onClick={() => setSuspendOpen(true)}>
                      تعلیق کانال
                    </Button>
                  )}
                  {d.status === 'disconnected' && (
                    <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={!!busy} onClick={() => setDeleteOpen(true)}>
                      پاک‌کردن رکورد
                    </Button>
                  )}
                </Stack>
              </Gate>
            </Stack>
            {invite && (
              <Alert severity="success" sx={{ mt: 1.5 }} action={
                <IconButton size="small" onClick={() => copy(invite, enqueueSnackbar)}><ContentCopyIcon fontSize="small" /></IconButton>
              }>
                لینک ورود (یک نفر، ۲۴ ساعت):{' '}
                <Link href={invite} target="_blank" rel="noopener" dir="ltr">{invite}</Link>
              </Alert>
            )}
            {d.purpose !== 'community' && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>انتشار خودکار محصولات</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                      خاموش = محصولات جدید پست نمی‌شوند و پست‌ها ویرایش نمی‌شوند (فروشنده هم می‌تواند دوباره روشنش کند)
                    </Typography>
                  </Box>
                  <Gate permission={MANAGE} mode="fallback" fallback={<Chip size="small" label={d.autoPublish ? 'روشن' : 'خاموش'} />}>
                    <Switch checked={d.autoPublish} disabled={!!busy || !d.active}
                      onChange={(e) => {
                        const v = e.target.checked;
                        run('auto', () => telegramChannelsService.setAutoPublish(d.chatId, v), v ? 'انتشار خودکار روشن شد' : 'انتشار خودکار خاموش شد');
                      }} />
                  </Gate>
                </Stack>
              </>
            )}
          </Section>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Section title="فروشگاه و مالک">
              <Kv k="فروشگاه" v={d.store ? <Link href={`${SITE_URL}/store/${d.store.slug}`} target="_blank" rel="noopener">{d.store.name}</Link> : 'کانال رسمی کازماشاپ'} />
              {d.store && <Kv k="وضعیت فروشگاه" v={d.store.status} />}
              <Kv k="ثبت‌کننده" v={d.owner?.name || '—'} />
              <Kv k="موبایل" v={<span dir="ltr">{d.owner?.phone || '—'}</span>} />
              <Kv k="تلگرام" v={d.owner?.telegram?.username ? <Link href={`https://t.me/${d.owner.telegram.username}`} target="_blank" rel="noopener" dir="ltr">@{d.owner.telegram.username}</Link> : '—'} />
              <Kv k="تاریخ اتصال" v={d.connectedAt ? formatDate(d.connectedAt) : '—'} />
              {d.discussionGroup && (
                <Kv k="گروه بحث متصل" v={d.discussionGroup.username ? <Link href={`https://t.me/${d.discussionGroup.username}`} target="_blank" rel="noopener" dir="ltr">@{d.discussionGroup.username}</Link> : d.discussionGroup.title || d.discussionGroup.chatId} />
              )}
            </Section>

            <Section title="اعضا و عملکرد (۳۰ روز)">
              <Kv k="کل اعضا (از تلگرام)" v={d.memberCount == null ? '—' : fa(d.memberCount.toLocaleString())} />
              <Kv k="اعضای ردیابی‌شده" v={fa(d.trackedMembers)} />
              {Array.isArray(s?.growth) && s.growth.find((g: any) => g.key === '30d') && (
                <Kv k="رشد خالص ۳۰ روز" v={fa(s.growth.find((g: any) => g.key === '30d').net)} />
              )}
              {conv && (
                <>
                  <Kv k="ورود به سایت از پست‌ها" v={fa(conv.visits ?? 0)} />
                  <Kv k="سفارش پرداخت‌شده" v={fa(conv.paidOrders ?? 0)} />
                  <Kv k="درآمد نسبت‌داده‌شده" v={toman(conv.revenue ?? 0)} />
                  <Kv k="نرخ تبدیل" v={`${fa(conv.conversionRate ?? 0)}٪`} />
                </>
              )}
              {cov && <Kv k="پوشش محصولات" v={`${fa(cov.published ?? 0)} از ${fa(cov.inScope ?? 0)} (${fa(cov.coveragePercent ?? 0)}٪)`} />}
              {s && <Kv k="خطای ۲۴ ساعت" v={fa(s.errors24h ?? 0)} />}
            </Section>
          </Box>

          <Section title="تنظیمات انتشار (تنظیم فروشنده)">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3 }}>
              <Kv k="حالت انتخاب محصول" v={d.settings.rules.mode === 'manual' ? `دستی (${fa(d.settings.rules.manualProducts)} محصول)` : 'خودکار طبق فیلتر'} />
              <Kv k="بعد از فروش" v={SOLD_OUT_LABEL[d.settings.rules.soldOutPolicy] || d.settings.rules.soldOutPolicy} />
              <Kv k="پست کاهش قیمت" v={d.settings.rules.priceDropPosts ? `روشن (حداقل ${fa(d.settings.rules.priceDropMinPercent)}٪)` : 'خاموش'} />
              <Kv k="دکمهٔ خرید" v={d.settings.template.buyButton === 'bot' ? 'ربات' : 'سایت (با ردیابی)'} />
              <Kv k="فیلتر قیمت" v={d.settings.filters.minPrice || d.settings.filters.maxPrice ? `${d.settings.filters.minPrice ? toman(d.settings.filters.minPrice) : '—'} تا ${d.settings.filters.maxPrice ? toman(d.settings.filters.maxPrice) : '—'}` : 'ندارد'} />
              <Kv k="فیلتر دسته / نوع محصول" v={`${fa(d.settings.filters.categories)} / ${fa(d.settings.filters.accountTypes)}`} />
              <Kv k="خلاصهٔ دوره‌ای" v={d.settings.digest.mode === 'off' ? 'خاموش' : d.settings.digest.mode === 'daily' ? 'روزانه' : 'هفتگی'} />
              <Kv k="ساعات سکوت" v={d.settings.schedule.quietFrom != null ? `${fa(d.settings.schedule.quietFrom)} تا ${fa(d.settings.schedule.quietTo ?? 0)}` : 'ندارد'} />
              <Kv k="لینک دعوت / رفرال" v={d.settings.referralEnabled ? `روشن (${fa(d.settings.referralQualifyDays)} روز)` : 'خاموش'} />
              <Kv k="کد تخفیف اعضا" v={d.settings.memberDiscount ? 'دارد' : 'ندارد'} />
              {d.settings.moderationMode && <Kv k="مدیریت گروه" v={MODERATION_LABEL[d.settings.moderationMode]} />}
            </Box>
          </Section>

          <Section title="آخرین پست‌ها">
            {d.posts.length === 0 ? (
              <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>هنوز پستی ثبت نشده</Typography>
            ) : (
              <Stack divider={<Divider flexItem />}>
                {d.posts.map((p) => (
                  <Stack key={p.id} direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ py: 0.75 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{p.title || (p.kind === 'broadcast' ? 'پست دستی' : 'پست')}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {formatDate(p.createdAt)} · {fa(p.clicks)} کلیک · {fa(p.visits)} ورود · {fa(p.reactions)} ری‌اکشن
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Chip size="small" label={POST_STATUS_LABEL[p.status] || p.status} sx={{ height: 20, fontSize: '0.65rem' }} />
                      {p.url && (
                        <IconButton size="small" component="a" href={p.url} target="_blank" rel="noopener">
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Section>

          <Section title="رویدادهای اخیر کانال">
            {d.activity.length === 0 ? (
              <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>رویدادی ثبت نشده</Typography>
            ) : (
              <Stack gap={0.75} sx={{ maxHeight: 280, overflowY: 'auto' }}>
                {d.activity.map((a) => (
                  <Stack key={a.id} direction="row" gap={1} alignItems="flex-start">
                    <Chip size="small" color={ACTIVITY_COLOR[a.level]} variant={a.level === 'info' ? 'outlined' : 'filled'} label={a.type} sx={{ height: 20, fontSize: '0.62rem', flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.78rem' }}>{a.message}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{formatDate(a.createdAt)}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Section>

          <Section title="یادداشت داخلی ادمین (فروشنده نمی‌بیند)">
            <Gate permission={MANAGE} mode="fallback" fallback={<Typography sx={{ fontSize: '0.8rem' }}>{d.adminNote || '—'}</Typography>}>
              <TextField fullWidth multiline minRows={2} value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 2000 }} placeholder="مثلاً: قرار همکاری تبلیغاتی، تذکر داده شد، …" />
              <Button size="small" variant="contained" sx={{ mt: 1 }} disabled={!!busy || note === (d.adminNote || '')}
                onClick={() => run('note', () => telegramChannelsService.saveNote(d.chatId, note), 'یادداشت ذخیره شد')}>
                ذخیرهٔ یادداشت
              </Button>
            </Gate>
          </Section>
        </Stack>
      )}

      <ConfirmDialog
        open={suspendOpen}
        title="تعلیق کانال"
        message="تا رفع تعلیق، هیچ محصولی در این کانال پست یا ویرایش نمی‌شود و فروشنده نمی‌تواند آن را دوباره فعال کند (حتی با ادمین‌کردن دوبارهٔ ربات). دلیل به فروشنده نشان داده می‌شود."
        onConfirm={async () => {
          if (!d) return;
          if (!suspendReason.trim()) {
            enqueueSnackbar('دلیل تعلیق را بنویسید', { variant: 'warning' });
            return;
          }
          await run('suspend', () => telegramChannelsService.suspend(d.chatId, suspendReason.trim()));
          setSuspendOpen(false);
          setSuspendReason('');
        }}
        onCancel={() => { setSuspendOpen(false); setSuspendReason(''); }}
        loading={busy === 'suspend'}
        confirmText="تعلیق"
        confirmColor="error"
      >
        <TextField fullWidth autoFocus label="دلیل تعلیق" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} inputProps={{ maxLength: 500 }} multiline minRows={2} />
      </ConfirmDialog>

      <ConfirmDialog
        open={deleteOpen}
        title="پاک‌کردن رکورد کانال"
        message="ربات از این چت حذف شده است. رکورد و صف پست‌های در انتظارش پاک می‌شود؛ اگر فروشنده دوباره ربات را ادمین کند از نو ثبت می‌شود."
        onConfirm={async () => {
          if (!d) return;
          setBusy('delete');
          try {
            await telegramChannelsService.remove(d.chatId);
            enqueueSnackbar('رکورد کانال پاک شد', { variant: 'success' });
            setDeleteOpen(false);
            onChanged();
            onClose();
          } catch (e) {
            enqueueSnackbar(errMsg(e), { variant: 'error' });
          } finally {
            setBusy(null);
          }
        }}
        onCancel={() => setDeleteOpen(false)}
        loading={busy === 'delete'}
        confirmText="پاک کن"
        confirmColor="error"
      />
    </ResponsiveModal>
  );
};

// ───────────────────────────────────────────── صفحه

const TelegramChannelsManagement: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [ov, setOv] = useState<TgOverview | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [openChat, setOpenChat] = useState<number | null>(null);

  const loadOverview = useCallback(async () => {
    try {
      setOv(await telegramChannelsService.overview());
    } catch {
      /* کارت‌ها در حالت بارگذاری می‌مانند */
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const refreshAll = () => {
    loadOverview();
    setReloadKey((k) => k + 1);
  };

  const coverage = ov && ov.totalActiveStores > 0 ? Math.round((ov.storesConnected / ov.totalActiveStores) * 100) : 0;

  return (
    <Box>
      <PageHeader
        title="کانال‌های تلگرام فروشگاه‌ها"
        subtitle="کدام فروشگاه‌ها کانال/گروه تلگرامشان را به کازماشاپ وصل کرده‌اند، آدرس کانال، سلامت، عملکرد و مدیریت"
        actions={[{ label: 'بروزرسانی', icon: <RefreshIcon />, variant: 'outlined', onClick: refreshAll }]}
      />

      {ov && !ov.bridge.ok && (
        <Alert severity="error" sx={{ mb: 2 }}>
          سرویس ربات تلگرام در دسترس نیست: {ov.bridge.message} — تا وصل نشود هیچ پستی منتشر نمی‌شود و «بررسی سلامت» کار نمی‌کند.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5, mb: 2 }}>
        <StatsCard
          title="فروشگاه متصل"
          value={fa(ov?.storesConnected ?? 0)}
          subtitle={ov ? `از ${fa(ov.totalActiveStores)} فروشگاه فعال (${fa(coverage)}٪)` : undefined}
          loading={!ov}
          onClick={() => setTab(1)}
        />
        <StatsCard
          title="کانال / گروه"
          value={fa(ov?.total ?? 0)}
          subtitle={ov ? `${fa(ov.channels)} کانال · ${fa(ov.groups)} گروه · ${fa(ov.platform)} رسمی` : undefined}
          loading={!ov}
        />
        <StatsCard title="کل اعضا" value={fa((ov?.totalMembers ?? 0).toLocaleString())} subtitle="مجموع کانال‌های فعال" loading={!ov} />
        <StatsCard title="پست فعال" value={fa(ov?.activePosts ?? 0)} subtitle={ov ? `${fa(ov.autoPublishOff)} کانال با انتشار خاموش` : undefined} loading={!ov} />
        <StatsCard
          title="فروش ۳۰ روز از تلگرام"
          value={toman(ov?.revenue30d ?? 0)}
          subtitle={ov ? `${fa(ov.orders30d)} سفارش پرداخت‌شده` : undefined}
          loading={!ov}
        />
        <StatsCard
          title="نیازمند توجه"
          value={fa((ov?.issues ?? 0) + (ov?.suspended ?? 0))}
          subtitle={ov ? `${fa(ov.issues)} مشکل ردیابی · ${fa(ov.suspended)} معلق · ${fa(ov.disconnected)} قطع · ${fa(ov.errors24h)} خطای ۲۴س` : undefined}
          loading={!ov}
        />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="کانال‌ها و گروه‌های متصل" />
        <Tab label="فروشگاه‌های بدون کانال" />
      </Tabs>

      {tab === 0 && <ChannelsTab reloadKey={reloadKey} onOpen={setOpenChat} />}
      {tab === 1 && <NoChannelTab reloadKey={reloadKey} />}

      <ChannelDetailModal chatId={openChat} onClose={() => setOpenChat(null)} onChanged={refreshAll} />
    </Box>
  );
};

export default TelegramChannelsManagement;
